import React from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '../BrandLogo';
import { SectionLink } from '../SectionLink';

/**
 * Nutrition page — live and linked in the nav.
 *
 * Design intent (from the 2026-07-30 brainstorm + 2026-08-07 "dude facts" pass):
 *  - Lead with a scannable fast-facts strip (complete protein / whole food /
 *    nut-free / protein-forward) — concrete, punchy, and all defensible NOW.
 *  - Balanced-vs-keto macro triangle (calorie-share, label-level precision —
 *    recipe estimates, NOT lab-assayed; never game the +/-20% window).
 *  - A calorie-share comparison vs PB & hummus (feedback-macro-framing: always
 *    state the basis; the protein-leadership story only holds under calorie-share).
 *  - Complete-protein-by-design section: the amino-acid complementarity LOGIC is
 *    claimable now (quinoa = methionine rescue); the PDCAAS number is NOT until
 *    the amino-acid panel lands. Underclaim, never round to flatter.
 *  - "The panel": a friendly, non-legal nutrition panel for the current home
 *    batch (Lot9-T5 @ 55g serving) — honest estimates, framed as batch-variable
 *    (small-batch by hand), never rounded to flatter. NOT the FDA Nutrition
 *    Facts box; that gets built from lab + locked recipe at production.
 */

const foods: { x: number; y: number; label: string; anchor?: 'start' | 'middle' | 'end'; dx?: number; dy?: number }[] = [
  { x: 274, y: 112, label: 'chicken', dx: 6, dy: -4 },
  { x: 223, y: 112, label: 'seitan', anchor: 'end', dx: -5, dy: -6 },
  { x: 208, y: 146, label: 'greek yogurt', anchor: 'end', dx: -6, dy: -2 },
  { x: 313, y: 239, label: 'tofu', dx: 7, dy: -2 },
  { x: 346, y: 258, label: 'egg', dx: 7, dy: -2 },
  { x: 364, y: 283, label: 'cheddar', dx: 7, dy: -2 },
  { x: 339, y: 310, label: 'peanut butter', anchor: 'end', dx: -6, dy: -1 },
  { x: 291, y: 310, label: 'hummus', anchor: 'end', dx: -5, dy: 12 },
  { x: 357, y: 345, label: 'avocado', dx: 7, dy: 7 },
  { x: 124, y: 286, label: 'black beans', anchor: 'end', dx: -6, dy: -2 },
  { x: 143, y: 314, label: 'quinoa', dx: 7, dy: -6 },
  { x: 133, y: 326, label: 'corn', anchor: 'end', dx: -7, dy: 11 },
  { x: 90, y: 335, label: 'rice', anchor: 'end', dx: -6, dy: -2 },
  { x: 247, y: 252, label: 'edamame', dx: 7, dy: -3 },
];

// Calorie-share (% of calories from each macro). TrueNooch = recipe estimate;
// PB & hummus = published averages. See feedback-macro-framing in the brain.
const MACRO_ROWS: { name: string; p: number; c: number; f: number; highlight?: boolean; reference?: boolean }[] = [
  // The target shape — the Dietary Guidelines' balanced range, shown at its midpoint.
  { name: 'Balanced range', p: 21, c: 53, f: 26, reference: true },
  { name: 'TrueNooch', p: 31, c: 51, f: 18, highlight: true },
  { name: 'Peanut butter', p: 16, c: 13, f: 71 },
  { name: 'Hummus', p: 16, c: 27, f: 57 },
];

// Three shades of ONE green — the macros are three parts of one whole food, not a
// ranked palette. A single hue removes any good-vs-bad read; only proportion (the
// SHAPE) carries meaning. All three macros are healthy (Jarvis, 2026-08-11).
const MACRO = { protein: '#6FB380', carbs: '#4F9A6A', fat: '#3B7B57' };

function MacroCompare() {
  return (
    <div style={{ margin: '1.5rem 0 0.5rem', maxWidth: 560 }}>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', fontSize: 12, color: '#AEB7C2', marginBottom: 14 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: MACRO.protein }} />protein</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: MACRO.carbs }} />carbs</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: 3, background: MACRO.fat }} />fat</span>
      </div>
      {MACRO_ROWS.map((r) => (
        <div key={r.name} style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: r.reference ? 18 : 10 }}>
          <div
            style={{
              width: 108,
              flexShrink: 0,
              fontSize: 13,
              textAlign: 'right',
              color: r.highlight ? '#F5F0E8' : r.reference ? '#C9B98A' : '#AEB7C2',
              fontWeight: r.highlight ? 600 : 400,
              fontStyle: r.reference ? 'italic' : 'normal',
            }}
          >
            {r.name}
          </div>
          <div
            role="img"
            aria-label={
              r.reference
                ? `Balanced range (target shape): about ${r.p}% protein, ${r.c}% carbs, ${r.f}% fat, by calorie-share`
                : `${r.name}: ${r.p}% protein, ${r.c}% carbs, ${r.f}% fat, by calorie-share`
            }
            style={{
              flex: 1,
              display: 'flex',
              height: 24,
              borderRadius: 6,
              overflow: 'hidden',
              boxSizing: 'border-box',
              outline: r.highlight ? '1.5px solid rgba(245,240,232,0.6)' : !r.reference ? '1px solid rgba(245,240,232,0.09)' : 'none',
              outlineOffset: r.highlight ? 2 : 0,
              border: r.reference ? '1.5px dashed rgba(232,207,140,0.7)' : 'none',
            }}
          >
            <div style={{ width: `${r.p}%`, background: MACRO.protein, opacity: r.reference ? 0.5 : 1, boxShadow: 'inset -1.5px 0 0 rgba(13,27,42,0.5)' }} title={`protein ${r.p}%`} />
            <div style={{ width: `${r.c}%`, background: MACRO.carbs, opacity: r.reference ? 0.5 : 1, boxShadow: 'inset -1.5px 0 0 rgba(13,27,42,0.5)' }} title={`carbs ${r.c}%`} />
            <div style={{ width: `${r.f}%`, background: MACRO.fat, opacity: r.reference ? 0.5 : 1 }} title={`fat ${r.f}%`} />
          </div>
        </div>
      ))}
      <p style={{ fontSize: 11.5, color: '#7f8794', margin: '12px 0 0', maxWidth: 480, lineHeight: 1.5 }}>
        <em style={{ color: '#C9B98A' }}>Balanced range</em> = the U.S. Dietary Guidelines&rsquo; healthy macro
        split (carbs 45–65%, fat 20–35%, protein 10–35% of calories), shown at its midpoint. Bars are % of
        calories from each macronutrient; peanut butter and hummus are published averages.
      </p>
    </div>
  );
}

function MacroTriangle() {
  return (
    <svg
      viewBox="0 0 500 405"
      width="100%"
      role="img"
      aria-label="Macro triangle: a balanced range where TrueNooch sits, and a keto range in the fat corner where cheese, peanut butter and avocado cluster."
      style={{ fontFamily: 'inherit', maxWidth: 480, display: 'block', margin: '0 auto' }}
    >
      <polygon points="240,50 70,360 410,360" fill="none" stroke="#54637A" strokeWidth="1" />

      <ellipse cx="352" cy="308" rx="31" ry="35" fill="#E0803B" fillOpacity="0.15" stroke="#E0803B" strokeOpacity="0.5" strokeWidth="1.25" strokeDasharray="4 4" />
      <text x="352" y="284" textAnchor="middle" fontSize="12" fill="#E0803B">keto</text>

      <ellipse cx="194" cy="296" rx="37" ry="46" fill="#5DBE3A" fillOpacity="0.16" stroke="#5DBE3A" strokeOpacity="0.5" strokeWidth="1.25" strokeDasharray="4 4" />
      <text x="194" y="324" textAnchor="middle" fontSize="12" fill="#5DBE3A">balanced</text>

      <text x="240" y="38" textAnchor="middle" fontSize="13" fill="#D8DEE6">protein</text>
      <text x="58" y="379" textAnchor="middle" fontSize="13" fill="#D8DEE6">carbs</text>
      <text x="422" y="379" textAnchor="middle" fontSize="13" fill="#D8DEE6">fat</text>

      {foods.map((f) => (
        <React.Fragment key={f.label}>
          <circle cx={f.x} cy={f.y} r="5" fill="#8b93a0" />
          <text x={f.x + (f.dx ?? 6)} y={f.y + (f.dy ?? -2)} textAnchor={f.anchor ?? 'start'} fontSize="10" fill="#AEB7C2">
            {f.label}
          </text>
        </React.Fragment>
      ))}

      <circle cx="184" cy="264" r="9" fill="#5DBE3A" stroke="#F5F0E8" strokeWidth="2.5" />
      <text x="174" y="252" textAnchor="end" fontSize="13" fontWeight="500" fill="#F5F0E8">TrueNooch</text>
    </svg>
  );
}

// The panels — a friendly, honest nutrition read per FORM. Deliberately NOT the
// FDA Nutrition Facts box: rounded, warm, and labeled as batch-variable.
//
// 2026-09-13 — was a single panel, back when the Round was the only thing we
// made. Now there are three forms and the page has to say so. The Round and the
// cubes are ONE panel because they are one food with the knife work done; the
// flour is its own, because drying and milling concentrate everything by weight
// and a 20 g scoop is nothing like a 55 g slice.
//
// Round/cubes: recipe estimates from the Lot9-T5 batch @ 55 g.
// Flour: bottom-up from the source ingredients' own panels x Lot 11 grams,
// converted at the 0.72 flour-per-Round yield
// (lab/product-development/tn-flour-nutrition-2026-09-11.md, 2026-09-11).
//
// Both are calculated, neither is lab-assayed, and protein on both is crude
// nitrogen x 6.25 — which OVERSTATES true protein, because nutritional yeast
// carries RNA nitrogen that is not protein. That is exactly what the
// amino-acid panel referenced above this section is being bought to settle, so
// every figure here rounds DOWN. Underclaim, never round to flatter.
interface PanelSpec {
  id: string;
  name: string;
  kicker: string;
  calories: string;
  basis: string;
  rows: { k: string; v: string; sub?: boolean; accent?: boolean }[];
  split: string;
  note?: string;
}

const PANELS: PanelSpec[] = [
  {
    id: 'round',
    name: 'The Round & the cubes',
    kicker: 'Made by hand · Denver, CO',
    calories: '≈ 200',
    basis: 'per 55 g serving',
    rows: [
      { k: 'Protein', v: '14 g', accent: true },
      { k: 'Total carbohydrate', v: '26 g' },
      { k: 'Dietary fiber', v: '7 g', sub: true },
      { k: 'Total sugars', v: '3 g', sub: true },
      { k: 'Total fat', v: '4.5 g' },
      { k: 'Sodium', v: '230 mg' },
    ],
    split: '≈ 31% protein · 51% carbs · 18% fat',
    note: 'One food, two shapes — a Round you slice and the same thing already cut. The numbers are identical.',
  },
  {
    id: 'flour',
    name: 'TrueNooch™ Flour',
    kicker: 'The same food, dried and milled',
    calories: '≈ 95',
    basis: 'per 20 g — about 2 rounded tablespoons',
    rows: [
      { k: 'Protein', v: '6.5 g', accent: true },
      { k: 'Total carbohydrate', v: '12 g' },
      { k: 'Dietary fiber', v: '2.9 g', sub: true },
      { k: 'Total sugars', v: '1.5 g', sub: true },
      { k: 'Total fat', v: '2 g' },
      { k: 'Sodium', v: '100 mg' },
    ],
    split: '≈ 29% protein · 50% carbs · 20% fat',
    note: 'Drying takes out water and nothing else, so everything else concentrates — by weight the flour is roughly two and a half times the Round. Worth knowing when you bake: it is a salted ingredient, so taste before you add more.',
  },
];

function NutritionPanel({ panel }: { panel: PanelSpec }) {
  return (
    <div
      style={{
        maxWidth: 420,
        flex: '1 1 340px',
        background: 'linear-gradient(180deg, rgba(93,190,58,0.06), rgba(255,255,255,0.02))',
        border: '1px solid #2f4a63',
        borderRadius: 18,
        padding: '22px 24px 20px',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12 }}>
        <div style={{ fontSize: 17, fontWeight: 600, color: '#F5F0E8' }}>{panel.name}</div>
      </div>
      <div style={{ fontSize: 13, color: '#95A0AE', marginTop: 2 }}>{panel.kicker}</div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, margin: '16px 0 4px' }}>
        <span style={{ fontSize: 40, fontWeight: 700, color: '#F5F0E8', lineHeight: 1 }}>{panel.calories}</span>
        <span style={{ fontSize: 14, color: '#AEB7C2' }}>calories</span>
      </div>
      <div style={{ fontSize: 13, color: '#95A0AE', marginBottom: 14 }}>{panel.basis}</div>

      <div style={{ borderTop: '1px solid rgba(245,240,232,0.12)' }}>
        {panel.rows.map((r) => (
          <div
            key={r.k}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              padding: '9px 0',
              borderBottom: '1px solid rgba(245,240,232,0.08)',
              paddingLeft: r.sub ? 16 : 0,
            }}
          >
            <span style={{ fontSize: r.sub ? 13.5 : 14.5, color: r.sub ? '#95A0AE' : '#D3DAE2', fontWeight: r.accent ? 600 : 400 }}>
              {r.k}
            </span>
            <span style={{ fontSize: r.sub ? 13.5 : 14.5, color: r.accent ? '#8FD86C' : '#E8EDF2', fontWeight: r.accent ? 700 : 500 }}>
              {r.v}
            </span>
          </div>
        ))}
      </div>

      <div style={{ marginTop: 14, fontSize: 13, color: '#AEB7C2', lineHeight: 1.55 }}>
        <strong style={{ color: '#8FD86C' }}>{panel.split}</strong>, by calories — balanced, not keto.
      </div>

      {panel.note && (
        <div style={{ marginTop: 12, fontSize: 12.5, color: '#95A0AE', lineHeight: 1.55 }}>
          {panel.note}
        </div>
      )}
    </div>
  );
}

// Nutrition-page hero — the trailhead scene (riders unloading, a loaf and a
// wrapped Round sliced open on the cooler lid) + the "sliceable" line. Turns
// the image into a quiet explanation: this food lives here, and you slice it.
function NutritionHero() {
  const bg = `url(${process.env.PUBLIC_URL}/images/brand/truenooch-trailhead-hero.webp)`;
  return (
    <div className="nutrition-hero" style={{ backgroundImage: bg }}>
      <div className="nutrition-hero-logo"><BrandLogo /></div>
      <div className="nutrition-hero-content">
        <h1>
          A single real food —<br />macro-balanced, and sliceable.
        </h1>
        <p className="nutrition-hero-sub">Slice it, and eat it however you like.</p>
      </div>
    </div>
  );
}

export function NutritionPage() {
  return (
    <>
      <NutritionHero />
      <section className="section fade-in" style={{ paddingTop: '3rem', paddingBottom: '1.5rem' }}>
        <div className="section-label">Nutrition</div>
        <h2>What&rsquo;s inside — and what &ldquo;balanced&rdquo; means</h2>
        <p style={{ maxWidth: '54ch' }}>
          TrueNooch™ is a short list of real ingredients, cooked into one whole food. Here&rsquo;s how its
          macros stack up, what &ldquo;balanced&rdquo; actually means, and what&rsquo;s inside.
        </p>
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem', paddingBottom: '1.5rem' }}>
        <div className="section-label">The shape of balanced</div>
        <h2>A bit of everything</h2>
        <p style={{ maxWidth: '54ch' }}>
          Most foods are mostly one thing — mostly fat, mostly carbs, mostly protein. TrueNooch™ is a
          bit of everything, in the range dietitians call balanced. Keto sits in the opposite corner,
          up in the fats — a different goal, not ours.
        </p>
        <div style={{ margin: '1.5rem auto 0.5rem' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, justifyContent: 'center', fontSize: 12, color: '#AEB7C2', marginBottom: 10 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: '50%', background: '#5DBE3A' }} />TrueNooch</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 11, borderRadius: 6, background: '#5DBE3A', opacity: 0.4, border: '1px solid #5DBE3A' }} />balanced</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 14, height: 11, borderRadius: 6, background: '#E0803B', opacity: 0.4, border: '1px solid #E0803B' }} />keto</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}><span style={{ width: 10, height: 10, borderRadius: '50%', background: '#8b93a0' }} />other foods</span>
          </div>
          <MacroTriangle />
          <p style={{ textAlign: 'center', fontSize: 11.5, color: '#7f8794', margin: '8px auto 0', maxWidth: 440 }}>
            Positioned by calorie-share (% of calories from each macro). &ldquo;Balanced&rdquo; is the
            Dietary Guidelines&rsquo; healthy range: carbs 45–65 · fat 20–35 · protein 10–35% of calories.
          </p>
        </div>
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem' }}>
        <div className="section-label">Build with it</div>
        <h2>A balanced shape you can build on</h2>
        <p style={{ maxWidth: '58ch' }}>
          No single macro runs the show — protein, carbs, and fat all land inside the healthy range.
          Slice it into what you&rsquo;re already making — a sandwich, a bowl, a board. You build the
          meal; TrueNooch keeps the balance.
        </p>
        <MacroCompare />
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem' }}>
        <div className="section-label">What &ldquo;balanced&rdquo; means</div>
        <h2>Not equal thirds — a range</h2>
        <p style={{ maxWidth: '58ch' }}>
          Balanced doesn&rsquo;t mean a third of each. The U.S. Dietary Guidelines put the healthy range at
          roughly 45–65% of calories from carbs, 20–35% from fat, and 10–35% from protein. That range
          is carb-forward by design — which is also how endurance bodies actually fuel. TrueNooch lands
          right in it, on the lean, low-fat side.
        </p>
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem' }}>
        <div className="section-label">The protein</div>
        <h2>Complete protein, by design</h2>
        <p style={{ maxWidth: '58ch' }}>
          Four protein sources go into TrueNooch: nutritional yeast, red lentils, kidney beans, and
          quinoa. The first three all run short on the same amino acid — methionine. Quinoa is rich in
          it. So the quinoa isn&rsquo;t in there for texture; it&rsquo;s there to cover exactly what the
          others miss.
        </p>
        <p style={{ maxWidth: '58ch' }}>
          That&rsquo;s what &ldquo;complete protein&rdquo; means here — not a word on a label, but how the
          recipe is built: each source patching another&rsquo;s gap. And it&rsquo;s protein from whole
          food, not a scoop of isolate stirred in.
        </p>
        <p style={{ maxWidth: '58ch', color: '#AEB7C2', fontSize: 14 }}>
          <em>
            The protein is being independently tested, amino acid by amino acid. Those results go on
            this page when they land.
          </em>
        </p>
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem' }}>
        <div className="section-label">The panel</div>
        <h2>The numbers</h2>
        <p style={{ maxWidth: '58ch' }}>
          TrueNooch™ is cooked by hand in small batches in Denver, so the numbers move a little from one
          batch to the next. Three forms, one recipe — the Round and the cubes are the same food, and
          the flour is that food with the water taken out.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start', margin: '1.5rem 0 0' }}>
          {PANELS.map((p) => (
            <NutritionPanel key={p.id} panel={p} />
          ))}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 28, alignItems: 'flex-start', margin: '2rem 0 0' }}>
          <div style={{ flex: 1, minWidth: 260, maxWidth: '42ch' }}>
            <p style={{ marginTop: 0, color: '#C4CDD8' }}>
              <strong style={{ color: '#E8CF8C' }}>Club members shape this.</strong> Our founding regulars
              taste the batches and tell us what&rsquo;s working — and these macros move with that feedback.
              If you want a say in the next batch, that&rsquo;s the club.
            </p>
            <Link className="club-cta" to="/club">Join the club &rarr;</Link>
          </div>
        </div>
      </section>

      <section className="section fade-in" style={{ paddingTop: '1rem', paddingBottom: '5rem' }}>
        <div className="section-label">What&rsquo;s inside</div>
        <h2>Real, whole ingredients</h2>
        <p style={{ maxWidth: '54ch' }}>
          Nutritional yeast, red lentils, tri-color quinoa, kidney beans, avocado oil,
          coconut sugar, sea salt.
        </p>
        <p style={{ maxWidth: '58ch' }}>
          What those whole ingredients bring: real fiber from the beans, lentils, and quinoa — the
          intact kind, not fiber added back in. A complete protein from the blend. And a small fat load
          that&rsquo;s all avocado oil — no seed-oil blend, no palm.
        </p>
        <p style={{ maxWidth: '58ch', color: '#C4CDD8' }}>
          No protein isolates, no gums, no emulsifiers, no &ldquo;natural flavors.&rdquo; If it&rsquo;s in
          there, you can name it.
        </p>

        <SectionLink to="/recipes">See ways to eat it</SectionLink>
      </section>
    </>
  );
}
